import React from "react";
import dayjs from "dayjs";
import WorkDay from "../../Types/WorkDay";

interface Props {
    days: WorkDay[];
}

const ACCENT   = "#c79656";
const PIN      = "#64748b";
const W        = 1000;
const H        = 320;
const PAD      = { t: 24, r: 24, b: 40, l: 56 };
const UW       = W - PAD.l - PAD.r;
const UH       = H - PAD.t - PAD.b;
const BASE_Y   = PAD.t + UH;

export default class PinCashOverviewGraph extends React.Component<Props> {
    private xFor(day: number, daysInMonth: number) {
        return PAD.l + (UW * (day - 1)) / Math.max(1, daysInMonth - 1);
    }

    private yFor(v: number, max: number) {
        return PAD.t + (1 - v / Math.max(1, max)) * UH;
    }

    private linePath(pts: Array<[number, number]>): string {
        if (pts.length === 0) return "";
        let d = `M ${pts[0][0]} ${pts[0][1]}`;
        for (let i = 1; i < pts.length; i++) d += ` L ${pts[i][0]} ${pts[i][1]}`;
        return d;
    }

    private areaPath(pts: Array<[number, number]>): string {
        if (pts.length === 0) return "";
        const line = this.linePath(pts);
        return `${line} L ${pts[pts.length - 1][0]} ${BASE_Y} L ${pts[0][0]} ${BASE_Y} Z`;
    }

    private formatEur(n: number) {
        if (n >= 1000) return `€${(n / 1000).toFixed(1)}k`;
        return `€${n.toFixed(2)}`;
    }

    public render() {
        const { days } = this.props;

        if (!days || days.length === 0) {
            return (
                <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 13, color: "rgba(0,0,0,0.35)", userSelect: "none" }}>No data this month</span>
                </div>
            );
        }

        const monthStart  = dayjs(days[0].Date).startOf("month");
        const daysInMonth = monthStart.daysInMonth();
        const monthNumber = monthStart.format("MM")

        const sorted = [...days]
            .filter(d => dayjs(d.Date).isSame(monthStart, "month"))
            .sort((a, b) => dayjs(a.Date).valueOf() - dayjs(b.Date).valueOf());

        let runCash = 0, runPin = 0;
        const cashPts: Array<[number, number]> = [];
        const pinPts:  Array<[number, number]> = [];

        for (const d of sorted) {
            const cash = d.IsClosed ? 0 : (d.TipCash ?? 0);
            const pin  = d.IsClosed ? 0 : (d.TipPin  ?? 0);
            runCash += cash;
            runPin  += pin;
            const x = this.xFor(dayjs(d.Date).date(), daysInMonth);
            if (cash > 0) cashPts.push([x, runCash]);
            if (pin  > 0) pinPts.push([x, runPin]);
        }

        const maxValue  = Math.max(1, runCash, runPin);
        const resolvedCash = cashPts.map(([x, v]) => [x, this.yFor(v, maxValue)] as [number, number]);
        const resolvedPin  = pinPts.map(([x, v])  => [x, this.yFor(v, maxValue)] as [number, number]);

        const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(f * maxValue));
        const xTicks = Array.from({ length: Math.min(7, daysInMonth) }, (_, i) =>
            Math.round((i * (daysInMonth - 1)) / (Math.min(7, daysInMonth) - 1 || 1)) + 1
        );

        const cashLinePath = this.linePath(resolvedCash);
        const pinLinePath  = this.linePath(resolvedPin);
        const cashAreaPath = this.areaPath(resolvedCash);
        const pinAreaPath  = this.areaPath(resolvedPin);

        const lastCash = resolvedCash[resolvedCash.length - 1];
        const lastPin  = resolvedPin[resolvedPin.length - 1];

        return (
            <div style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                overflow: "hidden",
                boxSizing: "border-box",
            }}>
                {/* Legend */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 4, height: 16, borderRadius: 4, background: ACCENT }} />
                        <span style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                            Overview for {monthStart.format("MMMM YYYY")}
                        </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}></div>
                    <div style={{ display: "flex", gap: 8 }}>
                        {[{ color: ACCENT, label: "Cash" }, { color: PIN, label: "PIN" }].map(({ color, label }) => (
                            <div
                                key={label}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "4px 10px", borderRadius: 999,
                                    background: "rgba(0,0,0,0.03)",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    fontSize: 11, fontWeight: 600,
                                    color: "rgba(0,0,0,0.55)",
                                    userSelect: "none",
                                }}
                            >
                                <svg width="18" height="4" style={{ display: "block" }}>
                                    <line x1="0" y1="2" x2="18" y2="2" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
                                </svg>
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Chart */}
                <svg
                    viewBox={`0 0 ${W} ${H}`}
                    style={{ flex: 1, minHeight: 0, width: "100%", display: "block" }}
                    preserveAspectRatio="xMidYMid meet"
                >
                    <defs>
                        <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor={ACCENT} stopOpacity="0.20" />
                            <stop offset="100%" stopColor={ACCENT} stopOpacity="0.01" />
                        </linearGradient>
                        <linearGradient id="pinGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor={PIN}    stopOpacity="0.16" />
                            <stop offset="100%" stopColor={PIN}    stopOpacity="0.01" />
                        </linearGradient>
                        <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2.5" result="blur" />
                            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                    </defs>

                    {/* Horizontal grid lines */}
                    {yTicks.map((v, i) => {
                        const y = this.yFor(v, maxValue);
                        const isBase = i === 0;
                        return (
                            <g key={`gy-${i}`}>
                                <line
                                    x1={PAD.l} y1={y} x2={W - PAD.r} y2={y}
                                    stroke={isBase ? "rgba(0,0,0,0.20)" : "rgba(0,0,0,0.07)"}
                                    strokeWidth={isBase ? 2 : 1}
                                    strokeDasharray={isBase ? undefined : "4 6"}
                                />
                                <text
                                    x={PAD.l - 10} y={y + 4}
                                    textAnchor="end"
                                    style={{ fontSize: 11, fill: "rgba(0,0,0,0.45)", userSelect: "none" as const, fontVariantNumeric: "tabular-nums" }}
                                >
                                    {this.formatEur(v)}
                                </text>
                            </g>
                        );
                    })}

                    {/* X axis ticks */}
                    {xTicks.map(dayNo => {
                        const x = this.xFor(dayNo, daysInMonth);
                        return (
                            <g key={`tx-${dayNo}`}>
                                <line x1={x} y1={BASE_Y} x2={x} y2={BASE_Y + 5} stroke="rgba(0,0,0,0.20)" strokeWidth={1} />
                                <text
                                    x={x} y={H - 10}
                                    textAnchor="middle"
                                    style={{ fontSize: 11, fill: "rgba(0,0,0,0.45)", userSelect: "none" as const }}
                                >
                                    {dayNo}/{monthNumber}
                                </text>
                            </g>
                        );
                    })}

                    {/* Left axis */}
                    <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={BASE_Y} stroke="rgba(0,0,0,0.20)" strokeWidth={2} />

                    {/* Area fills */}
                    {pinAreaPath  && <path d={pinAreaPath}  fill="url(#pinGrad)"  stroke="none" />}
                    {cashAreaPath && <path d={cashAreaPath} fill="url(#cashGrad)" stroke="none" />}

                    {/* Lines */}
                    {pinLinePath && (
                        <path d={pinLinePath} fill="none" stroke={PIN} strokeWidth={3.5} strokeLinejoin="round" strokeLinecap="round" />
                    )}
                    {cashLinePath && (
                        <path d={cashLinePath} fill="none" stroke={ACCENT} strokeWidth={3.5} strokeLinejoin="round" strokeLinecap="round" />
                    )}

                    {/* Data point dots */}
                    {resolvedPin.map(([x, y], i) => (
                        <circle key={`pd-${i}`} cx={x} cy={y} r={4} fill={PIN} opacity={0.7} />
                    ))}
                    {resolvedCash.map(([x, y], i) => (
                        <circle key={`cd-${i}`} cx={x} cy={y} r={4} fill={ACCENT} opacity={0.7} />
                    ))}

                    {/* End-of-line value labels */}
                    {lastPin && runPin > 0 && (
                        <text
                            x={lastPin[0] + 8} y={lastPin[1] + 4}
                            style={{ fontSize: 11, fontWeight: "bold", fill: PIN, userSelect: "none" as const }}
                        >
                            {this.formatEur(runPin)}
                        </text>
                    )}
                    {lastCash && runCash > 0 && (
                        <text
                            x={lastCash[0] + 8} y={lastCash[1] + 4}
                            style={{ fontSize: 11, fontWeight: "bold", fill: ACCENT, userSelect: "none" as const }}
                        >
                            {this.formatEur(runCash)}
                        </text>
                    )}
                </svg>
            </div>
        );
    }
}